import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CreditCardSelector from '../CreditCardSelector';
import PaymentTypeButton from './components/PaymentTypeButton';
import BankAccountSection from './components/BankAccountSection';
import PaymentFeeDisclaimer from './components/PaymentFeeDisclaimer';
import DataFieldsSelector from '../DataFieldsSelector';
import { useStripe } from '@stripe/react-stripe-js';
import withStripe from '../CreditCardSelector/hoc/withStripe';
import { setValueToStore } from '../../../sharedStore';
import {
  PAYMENT_TYPE_CARD,
  PAYMENT_TYPE_BANK,
} from '../../../controllers/donations_controller';
import { buildFeeText } from './utils';

function PaymentCheckoutSelector({
  withCard,
  withAch,
  withDataFields,
  dataFieldsProps,
  font,
  response,
  achPaymentsTermsUrl,
}) {
  const stripe = useStripe();
  const { amount, paymentMethods } = response.attributes;

  const [paymentType, setPaymentType] = React.useState(withCard ? PAYMENT_TYPE_CARD : PAYMENT_TYPE_BANK);
  const [connectedBankInfo, setConnectedBankInfo] = React.useState(false);
  const [hideCardFee, setHideCardFee] = React.useState(false);
  const [cardFeeText, setCardFeeText] = React.useState(buildFeeText(paymentMethods.find(m => m.kind === PAYMENT_TYPE_CARD)));

  const bankAccountFeeText = React.useMemo(() => {
    return buildFeeText(paymentMethods.find(m => m.kind === PAYMENT_TYPE_BANK));
  }, [paymentMethods]);

  const paymentMethod = React.useMemo(
    () => paymentMethods.find((m) => m.kind === paymentType),
    [paymentMethods, paymentType],
  );

  React.useEffect(() => {
    setValueToStore('amount', amount);
    setValueToStore('paymentMethods', paymentMethods);
    setValueToStore('selectedPaymentMethod', paymentMethod);
  }, []);

  const handleTypeCardClick = (type) => () => {
    if (type !== paymentType) {
      setHideCardFee(false);
      setPaymentType(type);
    }
  };

  const handleCreatePaymentMethod = (stripePaymentMethod, submitButton) => {
    const paymentMethodType = stripePaymentMethod.type;
    const paymentMethodCardFunding = stripePaymentMethod.card.funding;

    const selectedPaymentMethod = paymentMethods.find(m => m.kind === paymentMethodType && m.card_funding == paymentMethodCardFunding);
    const noFee = !selectedPaymentMethod?.fee || selectedPaymentMethod?.fee === '$0';

    setValueToStore('selectedPaymentMethod', selectedPaymentMethod);
    setHideCardFee(noFee);
    setCardFeeText(buildFeeText(selectedPaymentMethod));

    if (selectedPaymentMethod?.total) {
      const buttonContent = submitButton.dataset.originalContent.replace(amount, selectedPaymentMethod.total);
      submitButton.innerHTML = buttonContent;
    }
  };

  const disclaimerHtml = React.useMemo(() => {
    return paymentMethod?.disclaimer.richText.titleHtml.replace(
      '{payment_method_fee}',
      paymentMethod.fee,
    );
  }, [paymentMethod]);

  return (
    <Box>
      {withAch && withCard && (
        <Box sx={{ display: 'flex', gap: '16px', mt: 1, mb: 2 }}>
          <PaymentTypeButton
            icon={<CreditCardIcon />}
            label="Card"
            feeText={cardFeeText}
            selected={paymentType === PAYMENT_TYPE_CARD}
            onClick={handleTypeCardClick(PAYMENT_TYPE_CARD)}
            ariaLabel="select credit card"
            dataAction="donations#selectCardType"
          />
          <PaymentTypeButton
            icon={<AccountBalanceIcon />}
            label="Bank account"
            feeText={bankAccountFeeText}
            selected={paymentType === PAYMENT_TYPE_BANK}
            onClick={handleTypeCardClick(PAYMENT_TYPE_BANK)}
            ariaLabel="select bank account"
            dataAction="donations#selectBankType"
          />
        </Box>
      )}
      {withDataFields && (
        <Box sx={{ mb: 1, '& .disclaimer-checkbox__wrapper': { mt: '10px' } }}>
          <DataFieldsSelector {...dataFieldsProps} />
        </Box>
      )}
      {paymentType === PAYMENT_TYPE_CARD ? (
        <CreditCardSelector
          stripe={stripe}
          font={font}
          onCreatePaymentMethod={handleCreatePaymentMethod}
          setHideCardFee={setHideCardFee}
          dataFieldsProps={dataFieldsProps}
        />
      ) : (
        <BankAccountSection
          stripe={stripe}
          connectedBankInfo={connectedBankInfo}
          setConnectedBankInfo={setConnectedBankInfo}
          dataFieldsProps={dataFieldsProps}
        />
      )}
      <PaymentFeeDisclaimer
        paymentMethod={paymentMethod}
        disclaimerHtml={disclaimerHtml}
        paymentType={paymentType}
        hideCardFee={hideCardFee}
      />
      {paymentType === PAYMENT_TYPE_BANK && (
        <Typography
          sx={{
            fontFamily: 'inherit',
            fontSize: 12,
            color: '#bdbdbd',
            mt: 1,
          }}
          variant="body2"
          aria-label="ach disclaimer"
          data-css-selector="ach disclaimer"
        >
          By submitting this form, you agree to authorize payments pursuant to{' '}
          <Link
            sx={{
              fontFamily: 'inherit',
              fontSize: 12,
              color: 'var(--link-color)',
              whiteSpace: 'nowrap',
            }}
            target="_blank"
            underline="none"
            href={achPaymentsTermsUrl}
            variant="body2"
          >
            these terms.
          </Link>
        </Typography>
      )}
    </Box>
  );
}

export default withStripe(PaymentCheckoutSelector);
